import React, { useState } from 'react'
import { appConfirm } from '../../../../components/AppConfirmModal'
import { tokenStatus, tokenBadgeClasses } from '../../services/telephonyHelpers'

/**
 * AppRegistryCard
 * ---------------
 * The heart of the multi-app model. Lists every Exotel app under the tenant,
 * lets an admin create a new one (App Name), and selects the "active" app
 * whose agents are shown below.
 *
 * Webhook URL Management:
 * Each app card shows its webhook registration status and provides a panel
 * to re-register webhooks with a custom base URL (e.g. ngrok for dev, or
 * production domain). This is needed when the backend is behind a tunnel.
 */
export default function AppRegistryCard({
  apps,
  appsLoading,
  selectedAppId,
  onSelectApp,
  creating,
  appMsg,
  setAppMsg,
  onCreateApp,
  onDeleteApp,
  onReregisterWebhooks,  // async (appId, baseUrl) => { success, message }
}) {
  const [newApp, setNewApp] = useState({ app_name: '' })
  const [showForm, setShowForm] = useState(false)
  // Webhook re-register panel state — per-app (keyed by app.id)
  const [webhookPanelAppId, setWebhookPanelAppId] = useState(null)
  const [webhookUrl, setWebhookUrl] = useState('')
  const [webhookRegistering, setWebhookRegistering] = useState(false)
  const [webhookMsg, setWebhookMsg] = useState(null)

  const openWebhookPanel = (app, e) => {
    e.stopPropagation()
    setWebhookPanelAppId(app.id)
    setWebhookUrl('')
    setWebhookMsg(null)
  }

  const closeWebhookPanel = () => {
    setWebhookPanelAppId(null)
    setWebhookUrl('')
    setWebhookMsg(null)
  }

  const submitReregister = async (appId) => {
    if (!webhookUrl.trim()) {
      setWebhookMsg({ type: 'error', text: 'Enter the base URL (e.g. https://abc123.ngrok.io)' })
      return
    }
    if (!onReregisterWebhooks) return
    setWebhookRegistering(true)
    setWebhookMsg(null)
    try {
      const res = await onReregisterWebhooks(appId, webhookUrl.trim())
      setWebhookMsg({ type: 'success', text: res?.message || 'Webhooks re-registered successfully!' })
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Webhook registration failed'
      setWebhookMsg({ type: 'error', text: msg })
    } finally {
      setWebhookRegistering(false)
    }
  }

  const submitCreate = async () => {
    if (!newApp.app_name.trim()) {
      setAppMsg({ type: 'error', text: 'App name is required.' })
      return
    }
    const ok = await onCreateApp({ app_name: newApp.app_name.trim() })
    if (ok) {
      setNewApp({ app_name: '' })
      setShowForm(false)
    }
  }

  return (
    <div className="bg-surface-container-lowest p-6 rounded-2xl border border-outline-variant/25 shadow-xs space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-outline-variant/15 pb-4">
        <div>
          <h2 className="text-sm font-extrabold uppercase tracking-wider font-display text-on-surface">Exotel CPaaS Apps</h2>
          <p className="text-xs text-on-surface-variant font-medium mt-0.5">
            Exotel Apps under your account. Select an app to manage its webhooks and provisioned agents.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm((v) => !v)}
          className="px-4 py-2 rounded-xl bg-primary hover:bg-primary/90 text-on-primary font-bold text-xs shadow-md transition-colors flex items-center gap-2"
        >
          <span className="material-symbols-outlined text-base">{showForm ? 'close' : 'add_circle'}</span>
          <span>{showForm ? 'Cancel' : 'New app'}</span>
        </button>
      </div>

      {appMsg && (
        <div
          className={`p-3.5 rounded-xl border text-xs font-semibold flex items-center justify-between ${
            appMsg.type === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-base">
              {appMsg.type === 'success' ? 'check_circle' : 'error'}
            </span>
            <span>{appMsg.text}</span>
          </div>
          <button type="button" onClick={() => setAppMsg(null)} className="font-bold ml-2">✕</button>
        </div>
      )}

      {/* Create form */}
      {showForm && (
        <div className="rounded-xl border border-outline-variant/30 bg-surface-container-low/50 p-4 space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-on-surface uppercase tracking-wider block">App name</label>
            <input
              type="text"
              value={newApp.app_name}
              onChange={(e) => setNewApp({ ...newApp, app_name: e.target.value })}
              placeholder="e.g. Sales Call Centre"
              className="w-full p-2.5 rounded-xl border border-outline-variant/40 bg-surface-container-low text-xs text-on-surface focus:ring-2 focus:ring-primary focus:outline-none"
            />
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={submitCreate}
              disabled={creating}
              className="px-5 py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-on-primary font-bold text-xs shadow-md transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-base">add</span>
              <span>{creating ? 'Creating app…' : 'Create app'}</span>
            </button>
          </div>
          <p className="text-[11px] text-on-surface-variant">
            Runs the customer-token → create-app steps and stores the returned App ID and secret.
          </p>
        </div>
      )}

      {/* App list */}
      {appsLoading ? (
        <div className="py-8 text-center text-xs text-on-surface-variant">Loading apps…</div>
      ) : apps.length === 0 ? (
        <div className="py-10 text-center space-y-2">
          <span className="material-symbols-outlined text-3xl text-on-surface-variant">dialpad</span>
          <p className="text-xs text-on-surface-variant">
            No apps yet. Create one to start provisioning agents.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {apps.map((app) => {
            const selected = app.id === selectedAppId
            const tok = tokenStatus(app.app_token_expires_at)
            return (
              <div
                key={app.id}
                role="button"
                tabIndex={0}
                onClick={() => onSelectApp(app.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onSelectApp(app.id)
                  }
                }}
                className={`text-left rounded-xl border p-4 transition-colors cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                  selected
                    ? 'border-primary bg-primary/5 ring-1 ring-primary'
                    : 'border-outline-variant/30 bg-surface-container-low/40 hover:bg-surface-container-low'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-bold text-on-surface truncate">{app.app_name || 'Unnamed app'}</h4>
                      {selected && (
                        <span className="text-[10px] font-bold text-primary uppercase tracking-wider">Selected</span>
                      )}
                    </div>
                    <p className="text-[10px] font-mono text-on-surface-variant/70 mt-0.5 truncate">
                      {app.app_id}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <span
                      className={`px-2 py-0.5 rounded-full border text-[10px] font-bold ${tokenBadgeClasses(tok.state)}`}
                    >
                      {tok.label}
                    </span>
                    <span className="text-[11px] text-on-surface-variant">
                      {app.agent_count ?? 0} agent{(app.agent_count ?? 0) === 1 ? '' : 's'}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-outline-variant/20">
                  <div className="flex items-center gap-2 flex-wrap">
                    {app.webhooks_configured ? (
                      <span
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-medium"
                        title={app.webhooks_configured_at ? `Webhooks connected on ${new Date(app.webhooks_configured_at).toLocaleString()}` : 'Webhooks configured in Exotel'}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        Webhooks Synced
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-medium"
                        title="Webhooks pending registration with Exotel"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                        Webhooks Pending
                      </span>
                    )}

                    {/* Re-register webhooks button (for ngrok/production URL change) */}
                    {onReregisterWebhooks && (
                      <button
                        type="button"
                        onClick={(e) => openWebhookPanel(app, e)}
                        title="Re-register webhook URLs with a new base URL (e.g. ngrok)"
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-surface-container-low border border-outline-variant/30 text-on-surface-variant hover:bg-primary/10 hover:text-primary hover:border-primary/30 text-[10px] font-medium cursor-pointer transition-colors"
                      >
                        <span className="material-symbols-outlined text-xs">webhook</span>
                        Update URL
                      </button>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={async (e) => {
                      e.stopPropagation()
                      const confirmed = await appConfirm({
                        title: 'Delete Exotel App',
                        message: `Are you sure you want to delete "${app.app_name || app.app_id}"? All provisioned agents under this App will be unmapped. This action cannot be undone.`,
                        confirmText: 'Delete App',
                        variant: 'danger',
                      })
                      if (confirmed) {
                        onDeleteApp(app.id)
                      }
                    }}
                    className="px-2.5 py-1 rounded-lg border border-red-200 text-red-700 hover:bg-red-50 text-[11px] font-semibold inline-flex items-center gap-1 cursor-pointer transition-colors shrink-0"
                  >
                    <span className="material-symbols-outlined text-sm">delete</span>
                    Delete
                  </button>
                </div>

                {/* ── Webhook URL Re-register Panel ── */}
                {webhookPanelAppId === app.id && (
                  <div
                    className="mt-3 p-4 rounded-xl border border-primary/20 bg-primary/5 space-y-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-[11px] font-extrabold uppercase tracking-wider text-primary">Update Webhook Base URL</p>
                        <p className="text-[10px] text-on-surface-variant mt-0.5">
                          Enter your ngrok HTTPS URL or production domain. All webhook keys
                          (popup, callback, incomingCallHangup, missedCall) will be re-registered.
                        </p>
                      </div>
                      <button type="button" onClick={closeWebhookPanel} className="text-outline hover:text-on-surface transition-colors">
                        <span className="material-symbols-outlined text-base">close</span>
                      </button>
                    </div>

                    {webhookMsg && (
                      <div className={`p-2.5 rounded-lg border text-[11px] font-semibold flex items-center gap-2 ${
                        webhookMsg.type === 'success'
                          ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                          : 'bg-red-50 border-red-200 text-red-800'
                      }`}>
                        <span className="material-symbols-outlined text-sm">
                          {webhookMsg.type === 'success' ? 'check_circle' : 'error'}
                        </span>
                        {webhookMsg.text}
                      </div>
                    )}

                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant block">
                        Base URL
                      </label>
                      <input
                        type="url"
                        placeholder="https://abc123.ngrok-free.app"
                        value={webhookUrl}
                        onChange={(e) => { setWebhookUrl(e.target.value); setWebhookMsg(null) }}
                        className="w-full px-3 py-2 rounded-xl border border-outline-variant/40 bg-surface-container-lowest text-xs text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/30"
                      />
                      <p className="text-[10px] text-on-surface-variant">
                        Exotel will POST to: <span className="font-mono text-primary">{webhookUrl || 'https://your-url'}/telephony/webhook/popup?token=••••••••</span> (secured with tenant token)
                      </p>
                    </div>

                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={webhookRegistering || !webhookUrl.trim()}
                        onClick={() => submitReregister(app.id)}
                        className="flex-1 py-2 rounded-xl bg-primary hover:bg-primary/90 text-on-primary text-xs font-bold transition-colors disabled:opacity-50 cursor-pointer flex items-center justify-center gap-1.5"
                      >
                        <span className="material-symbols-outlined text-sm">{webhookRegistering ? 'sync' : 'webhook'}</span>
                        {webhookRegistering ? 'Registering…' : 'Re-register Webhooks'}
                      </button>
                      <button
                        type="button"
                        onClick={closeWebhookPanel}
                        className="px-4 py-2 rounded-xl border border-outline-variant/30 text-xs font-semibold text-on-surface-variant hover:bg-surface-container-low transition-colors cursor-pointer"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
